import axios, { AxiosResponse } from 'axios';

import { authFetch } from '.';
import { IPrepaidDealCost, IPrepaidDealCostsListVM, PrepaidDealCostStatusResponse } from '../models/agent-draws-and-deductions/prepaid-deal-cost';

const buildConfig = require('../buildSettings');
const apiBaseUrl = buildConfig.REACT_APP_API_URL;
const baseUrl = '/api/Admin/AgentDrawsAndDeductions';

export const PrepaidDealCostsAPI = {
  
  async getByAgentId(
    id: number,
    options: {
      page?: number;
      perPage?: number;
      sort?: string;
      search?: string;
      advancesFinancialStatusFilter?: number;
    }
  ): Promise<IPrepaidDealCostsListVM> {
    try {
      const { page = 1, perPage = 10, sort, search, advancesFinancialStatusFilter } = options;
      const qs = new URLSearchParams({
        page: String(page),
        perPage: String(perPage),
        ...(sort?.trim() ? { sort: sort.trim() } : {}),
        ...(search?.trim() ? { search: search.trim() } : {}),
        ...(advancesFinancialStatusFilter !== undefined ? { advancesFinancialStatusFilter: String(advancesFinancialStatusFilter) } : {}),
      }).toString();
      const response = await authFetch.get(`${baseUrl}/agent-deductions/${id}?${qs}`);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return Promise.reject(err?.response?.data);
      }
      return Promise.reject(err);
    }
  },

  /* statuses */
  async fetchStatuses(): Promise<PrepaidDealCostStatusResponse> {
    const response = await authFetch.get(`${baseUrl}/lookup/agent-deduction-enums`);
    return response.data;
  },

  /* create */
  async create(
    agentId: number,
    payload: {
      date: string;
      amount: number;
      fsReimbursementPercent: number;
      memo?: string;
      notes?: string;
    }
  ): Promise<IPrepaidDealCost> {
    try {
      const response = await authFetch.post(`${baseUrl}/agent-deductions/${agentId}`, {
        date: payload.date,
        amount: payload.amount,
        fsReimbursementPercent: payload.fsReimbursementPercent,
        memo: payload.memo,
        notes: payload.notes,
      });
      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return Promise.reject(err?.response?.data);
      }
      return Promise.reject(err);
    }
  },

  /* Update  */
  async update(
    agentDeductionId: number,
    payload: {
      date: string;
      amount: number;
      fsReimbursementPercent: number;
      memo?: string;
      notes?: string;
    }
  ): Promise<IPrepaidDealCost> {
    try {
      const response = await authFetch.put(`${baseUrl}/agent-deduction/${agentDeductionId}`, {
        date: payload.date,
        amount: payload.amount,
        fsReimbursementPercent: payload.fsReimbursementPercent,
        memo: payload.memo,
        notes: payload.notes,
      });
      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return Promise.reject(err?.response?.data);
      }
      return Promise.reject(err);
    }
  },

  /* Update the advances financial status of a prepaid deal cost (agent deduction) */
   
  async updateFinancialStatus(
    agentDeductionId: number,
    advancesFinancialStatusId: number
  ): Promise<IPrepaidDealCost> {
    try {
      const response = await authFetch.put(
        `${baseUrl}/agent-deduction/${agentDeductionId}/financial-status`,
        {
          advancesFinancialStatusId,
        }
      );
      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }
      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return Promise.reject(err?.response?.data);
      }
      return Promise.reject(err);
    }
  },

  /* file upload*/
  async uploadFile(agentDeductionId: number, file: File): Promise<number> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response: AxiosResponse<number> = await authFetch.post(
        `${baseUrl}/agent-deduction/${agentDeductionId}/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }

      return response.data;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return Promise.reject(err?.response?.data);
      }
      return Promise.reject(err);
    }
  },

  /* File download */
  async downloadFile(agentDeductionId: number, fileId: number): Promise<AxiosResponse<Blob>> {
    try {
      const response = await authFetch.getFile(
        `${baseUrl}/agent-deduction/${agentDeductionId}/file/${fileId}`,
        {
          responseType: 'blob',
        }
      );
      return response;
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return Promise.reject(err?.response?.data);
      }
      return Promise.reject(err);
    }
  },

  /* Get the direct URL for viewing/downloading a file (opens with proper Content-Disposition) */
  getFileUrl(agentDeductionId: number, fileId: number): string {
    return `${apiBaseUrl}${baseUrl}/agent-deduction/${agentDeductionId}/file/${fileId}`;
  },
};
